
import { useCallback, useState } from 'react';
import Link from '@/components/compat/Link';
import { Package } from 'lucide-react';
import { getPowerBySlug, type PowerInfo } from '@/lib/powers-data';
import {
  isCollectionPower,
  resolveCollectionForPower,
  getCollectionListItem,
  formatPowerSlugLabel,
} from '@/lib/collections-data';
import {
  GsImage,
  ORBITAL_ICON_BOX_CLASS,
  copyText,
  getPowerCodeFontSize,
  cn,
} from '@/components/help-center/latest-power-shared';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import {
  GALLERY_BOX_STYLE,
  PowerGalleryViewToggle,
  PawnBadgeIcon,
  galleryBadgeClass,
  galleryCodeTextClass,
  usePowerGalleryViewMode,
  type PowerGalleryViewMode,
} from '@/components/help-center/powers/power-gallery-view';
import {
  GalleryHoverOverlay,
  galleryItemHoverClass,
  galleryMediaHoverClass,
} from '@/components/help-center/powers/gallery-hover';

interface PowerCollectionSectionProps {
  power: PowerInfo;
}

function CardLabel({
  text,
  viewMode,
  isCopied,
  fontSize,
}: {
  text: string;
  viewMode: PowerGalleryViewMode;
  isCopied?: boolean;
  fontSize?: number;
}) {
  return (
    <div className={galleryBadgeClass(viewMode, isCopied)}>
      <span
        className={cn(
          'whitespace-nowrap font-poppins font-semibold leading-none',
          galleryCodeTextClass(viewMode)
        )}
        style={fontSize ? { fontSize: `${fontSize}px` } : undefined}
      >
        {text}
      </span>
    </div>
  );
}

function RequiredPowerCard({
  slug,
  viewMode,
}: {
  slug: string;
  viewMode: PowerGalleryViewMode;
}) {
  const linked = getPowerBySlug(slug);
  const label = linked?.name ?? formatPowerSlugLabel(slug);

  return (
    <Link
      href={`/powers/${slug}`}
      className={cn(
        ORBITAL_ICON_BOX_CLASS,
        'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3',
        galleryItemHoverClass(viewMode)
      )}
      style={GALLERY_BOX_STYLE[viewMode]}
      title={`Ver power ${label}`}
    >
      <GalleryHoverOverlay mode="view" />
      <GsImage
        code={slug}
        className={cn('flex h-14 w-14 items-center justify-center', galleryMediaHoverClass)}
        imgClassName="max-h-[52px] max-w-[52px] object-contain"
      />
      <CardLabel text={label} viewMode={viewMode} fontSize={getPowerCodeFontSize(label)} />
    </Link>
  );
}

function CollectionPawnCard({
  code,
  viewMode,
  copiedKey,
  onCopy,
}: {
  code: string;
  viewMode: PowerGalleryViewMode;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
}) {
  const itemKey = `collection-pawn-${code}`;
  const isCopied = copiedKey === itemKey;
  const chatCode = code.startsWith('(') ? code : `(${code})`;

  return (
    <button
      type="button"
      onClick={() => onCopy(itemKey, chatCode)}
      className={cn(
        ORBITAL_ICON_BOX_CLASS,
        'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3',
        galleryItemHoverClass(viewMode)
      )}
      style={GALLERY_BOX_STYLE[viewMode]}
      title={`Copiar ${chatCode}`}
    >
      <GalleryHoverOverlay mode="copy" isCopied={isCopied} />
      <GsImage
        code={code}
        className={cn('flex h-14 w-14 items-center justify-center', galleryMediaHoverClass)}
        imgClassName="max-h-[52px] max-w-[52px] object-contain"
      />
      <CardLabel
        text={code}
        viewMode={viewMode}
        isCopied={isCopied}
        fontSize={getPowerCodeFontSize(code)}
      />
    </button>
  );
}

export default function PowerCollectionSection({ power }: PowerCollectionSectionProps) {
  const { mode, setMode } = usePowerGalleryViewMode();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = useCallback(async (key: string, text: string) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 1800);
    }
  }, []);

  if (!isCollectionPower(power)) return null;

  const collection = resolveCollectionForPower(power);
  if (!collection) return null;

  const summary = getCollectionListItem(power.slug);
  const requiredCount = summary?.total.required ?? collection.required.length;
  const pawnsCount = summary?.total.pawns ?? collection.pawns.length;

  return (
    <section className="bg-white py-14 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50">
              <Package className="h-4.5 w-4.5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 font-poppins">
                Coleção
              </p>
              <h2 className="text-xl font-bold text-gray-900 font-poppins">
                Conteúdo da coleção
              </h2>
            </div>
          </div>
          <PowerGalleryViewToggle mode={mode} onChange={setMode} className="shrink-0" />
        </div>

        <div className="mb-10 flex flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-2.5">
            <MaskedSvgIcon src="/icons/power.svg" className="h-4 w-4 text-indigo-900" />
            <span className="font-poppins text-sm font-semibold text-indigo-900">
              {requiredCount} powers necessários
            </span>
          </div>
          <div className="inline-flex items-center gap-1 rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-2.5">
            <PawnBadgeIcon className="text-emerald-700" />
            <span className="font-poppins text-sm font-semibold leading-none text-emerald-800">
              {pawnsCount} peões exclusivos
            </span>
          </div>
        </div>

        {collection.required.length > 0 && (
          <div className="mb-12">
            <div className="mb-5">
              <h3 className="font-poppins text-base font-bold text-gray-900">
                Powers necessários
              </h3>
              <p className="text-sm text-gray-500 font-inter mt-1">
                Estes powers fazem parte da coleção {power.name}. Clique para ver cada um em detalhe.
              </p>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5">
              {collection.required.map((slug) => (
                <RequiredPowerCard key={slug} slug={slug} viewMode={mode} />
              ))}
            </div>
          </div>
        )}

        {collection.pawns.length > 0 && (
          <div>
            <div className="mb-5">
              <h3 className="font-poppins text-base font-bold text-gray-900">
                Peões da coleção
              </h3>
              <p className="text-sm text-gray-500 font-inter mt-1">
                Peões desbloqueados ao completar a coleção {power.name}. Clique para copiar o código.
              </p>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5">
              {collection.pawns.map((code) => (
                <CollectionPawnCard
                  key={code}
                  code={code}
                  viewMode={mode}
                  copiedKey={copiedKey}
                  onCopy={handleCopy}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
